// src/services/index.js
// Barrel export for the services layer.
// Import from subdomain for clarity, or '@services' for common utils.
//
// Domain imports:
//   import { fetchNWSAlerts }         from '@services/alerts'
//   import { getCurrentPosition }     from '@services/geolocation'
//   import { sendLocal, vibrate }     from '@services/notifications'
//   import { SyncQueue, APICache }    from '@services/storage/idb'
//   import { setupPushNotifications } from '@services/notifications/push'
//   import { computeConfidenceScore } from '@services/moderation'
//   import { cacheAreaTiles }         from '@services/maps'
//   import { fetchPointForecast }     from '@services/weather'

// Re-export so existing `from '@services'` imports keep working
export { proxyFetch, fetchNWSAlerts, fetchUSGSGauges, fetch511LA } from './alerts/index.js';
export { getCurrentPosition, watchPosition, haversineKm, haversineMiles, inferParish, inferZone, formatCoords, mapsDirectionsUrl } from './geolocation/index.js';
export { sendLocal, deliverEmergencyAlert, vibrate, vibrateAlert, vibrateStop, shareApp, requestPermission, getPermissionStatus } from './notifications/index.js';
export { SyncQueue, APICache, UserData, TileRegistry, NotificationHistory, openDB, getDBStats } from './storage/idb.js';
export { initFirebase, withRetry, getDB, getAuth, onConnectionChange, initFirebaseReconnect } from './storage/firebase.js';
export { registerSW, applyUpdate, sendToSW, prefetchTiles, getTileUrlsForArea } from './storage/sw.js';
export { computeConfidenceScore, isPostVisible, sortPosts, detectSpam, checkRateLimit, recordPostSubmission, validateSOSPost, recordSOSSubtype } from './moderation/index.js';
export { MAP_DEFAULTS, TILE_PROVIDERS, MAP_MODES, makeDivIcon } from './maps/index.js';
export { getRadarTileUrl, classifyFloodStage } from './weather/index.js';

// ── Time utilities ────────────────────────────────────────────────
export function timeAgo(ts, prefix = '') {
  if (!ts) return 'Update time unavailable';
  // Handle Firestore Timestamp objects, ISO strings, and numeric ms
  let ms;
  if (ts?.toMillis) ms = ts.toMillis();           // Firestore Timestamp
  else if (ts?.seconds) ms = ts.seconds * 1000;   // Firestore-like {seconds}
  else if (typeof ts === 'string') ms = Date.parse(ts); // ISO string
  else ms = Number(ts);                            // numeric ms
  if (!ms || isNaN(ms)) return 'Update time unavailable';
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 0)   return prefix ? `${prefix} just now` : 'just now';
  if (s < 60)  return prefix ? `${prefix} just now` : 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)  return prefix ? `${prefix} ${m}m ago` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return prefix ? `${prefix} ${h}h ago` : `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d > 7)   return '⚠ Data may be outdated';
  return prefix ? `${prefix} ${d}d ago` : `${d}d ago`;
}

// Semantic wrappers — use these instead of bare timeAgo for clarity
export function checkedAgo(ts)  { return timeAgo(ts, 'Checked'); }
export function fetchedAgo(ts)  { return timeAgo(ts, 'Fetched'); }
export function updatedAgo(ts)  { return timeAgo(ts, 'Updated'); }
export function postedAgo(ts)   { return timeAgo(ts, 'Posted'); }

export function formatTs(ts) {
  if (!ts) return '';
  const d    = new Date(ts);
  const now  = new Date();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (d.toDateString() === now.toDateString()) return 'Today ' + time;
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return 'Yesterday ' + time;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time;
}

// ── Offline init (called from main.jsx) ───────────────────────────
export async function initOffline() {
  const { registerSW } = await import('./storage/sw.js');
  await registerSW();

  window.addEventListener('online', () => {
    window.dispatchEvent(new CustomEvent('na-flush-queue'));
  });

  import('./storage/idb.js').then(({ APICache }) => {
    APICache.prune().then((n) => {
      if (n > 0) console.info('[IDB] Pruned ' + n + ' stale cache entries');
    }).catch(() => {});
  });

  import('./notifications/push.js').then(({ flushTopicQueue }) => {
    // Only flush if we have a valid stored token and notification permission
    if (localStorage.getItem('na-fcm-token') && Notification?.permission === 'granted') {
      flushTopicQueue().catch(() => {});
    }
  });
}

// Monitoring
export { initSentry, captureException, captureMessage, breadcrumb, identifyUser } from './monitoring/index.js';
